import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CircleMarker, Popup } from 'react-leaflet';
import { BarChart3 } from 'lucide-react';

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { PageSpinner } from '@/components/ui/Spinner';
import { MetroMap } from '@/components/map/MetroMap';
import { adminApi } from '@/api/endpoints/admin';

const PLATFORM_COLORS: Record<string, string> = {
  web: '#f59e0b',
  ios: '#3b82f6',
  android: '#22c55e',
};

function formatDate(dateStr: string) {
  // Handle both "2026-04-09" and "2026-04-09T05:00:00.000Z" formats
  const dateOnly = String(dateStr).split('T')[0];
  const d = new Date(dateOnly + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

export function AnalyticsPage() {
  const globalQuery = useQuery({
    queryKey: ['admin-analytics-global', 7],
    queryFn: () => adminApi.getGlobalAnalytics(7),
    refetchInterval: 60000,
  });

  const mapQuery = useQuery({
    queryKey: ['admin-analytics-map'],
    queryFn: () => adminApi.getAnalyticsMap(7),
    refetchInterval: 60000,
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dailyData: any[] = globalQuery.data?.dailyData ?? [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const topBars: any[] = globalQuery.data?.topBars ?? [];
  const totals = globalQuery.data?.totals ?? { specials: 0, events: 0, bars: 0, detailViews: 0, uniqueViewers: 0 };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapPoints: any[] = mapQuery.data?.points ?? [];

  const maxDailyViews = useMemo(
    () => Math.max(1, ...dailyData.map((d: { totalViews: number }) => d.totalViews)),
    [dailyData]
  );

  const totalImpressions = totals.specials + totals.events + totals.bars;
  const totalAllViews = totalImpressions + totals.detailViews;
  const viewTypeSplit = useMemo(() => {
    if (totalAllViews === 0) return [];
    return [
      { label: 'Specials', count: totals.specials, pct: Math.round((totals.specials / totalAllViews) * 100), color: 'bg-amber-500' },
      { label: 'Events', count: totals.events, pct: Math.round((totals.events / totalAllViews) * 100), color: 'bg-blue-500' },
      { label: 'Bars List', count: totals.bars, pct: Math.round((totals.bars / totalAllViews) * 100), color: 'bg-emerald-500' },
      { label: 'Bar Detail', count: totals.detailViews, pct: Math.round((totals.detailViews / totalAllViews) * 100), color: 'bg-purple-500' },
    ];
  }, [totals, totalAllViews]);

  if (globalQuery.isLoading) return <PageSpinner />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <BarChart3 className="h-6 w-6 text-accent-primary" />
          Analytics
        </h1>
        <p className="text-sm text-[var(--color-muted)] mt-1">Last 7 days</p>
      </div>

      {/* Daily Views Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Total Views Per Day</CardTitle>
        </CardHeader>
        <CardContent>
          {dailyData.length === 0 ? (
            <p className="text-[var(--color-muted)] text-center py-8">No data yet</p>
          ) : (
            <div className="flex items-end gap-1 h-40">
              {dailyData.map((day: { date: string; totalViews: number }) => (
                <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-xs text-[var(--color-muted)]">
                    {day.totalViews > 0 ? day.totalViews.toLocaleString() : ''}
                  </span>
                  <div
                    className="w-full rounded-t transition-all"
                    style={{
                      height: `${(day.totalViews / maxDailyViews) * 100}%`,
                      minHeight: day.totalViews > 0 ? '4px' : '0',
                      backgroundColor: 'var(--color-accent-primary)',
                      opacity: 0.7,
                    }}
                  />
                  <span className="text-[10px] text-[var(--color-muted)]">
                    {formatDate(day.date)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top Bars + View Type Split */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Bars */}
        <Card>
          <CardHeader>
            <CardTitle>Top Bars — Last 7 Days</CardTitle>
          </CardHeader>
          <CardContent>
            {topBars.length === 0 ? (
              <p className="text-[var(--color-muted)] text-center py-4">No data yet</p>
            ) : (
              <div className="space-y-2">
                {topBars.map((bar: { id: string; name: string; slug: string; totalImpressions: number; detailViews: number }, i: number) => (
                  <div key={bar.id} className="flex items-center gap-3 py-1">
                    <span className="text-sm font-medium text-[var(--color-muted)] w-6 text-right">{i + 1}.</span>
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/admin/bars/${bar.id}`}
                        className="text-sm font-medium text-accent-primary hover:underline truncate block"
                      >
                        {bar.name}
                      </Link>
                    </div>
                    <div className="text-right shrink-0">
                      <span className="text-sm font-semibold">{bar.totalImpressions.toLocaleString()}</span>
                      <span className="text-xs text-[var(--color-muted)] ml-1">views</span>
                      {bar.detailViews > 0 && (
                        <span className="text-xs text-[var(--color-muted)] ml-2">
                          ({bar.detailViews} detail)
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* View Type Split */}
        <Card>
          <CardHeader>
            <CardTitle>View Type Split</CardTitle>
          </CardHeader>
          <CardContent>
            {viewTypeSplit.length === 0 ? (
              <p className="text-[var(--color-muted)] text-center py-4">No data yet</p>
            ) : (
              <div className="space-y-4">
                {viewTypeSplit.map((type) => (
                  <div key={type.label}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium">{type.label}</span>
                      <span className="text-[var(--color-muted)]">
                        {type.count.toLocaleString()} ({type.pct}%)
                      </span>
                    </div>
                    <div className="h-3 bg-[var(--color-border)] rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${type.color}`}
                        style={{ width: `${type.pct}%` }}
                      />
                    </div>
                  </div>
                ))}
                <div className="pt-2 border-t border-[var(--color-border)] text-sm text-[var(--color-muted)]">
                  {totalAllViews.toLocaleString()} total views &middot; {totals.uniqueViewers.toLocaleString()} unique viewers
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Map */}
      <Card>
        <CardHeader>
          <CardTitle>User Locations</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[500px] rounded-lg overflow-hidden">
            <MetroMap center={[39.0, -95.7]} zoom={4}>
              {mapPoints.map((point: { lat: number; lng: number; platform: string; count: number; uniqueUsers: number }, i: number) => (
                <CircleMarker
                  key={`${point.lat}-${point.lng}-${point.platform}-${i}`}
                  center={[point.lat, point.lng]}
                  radius={Math.max(4, Math.min(20, Math.sqrt(point.count) * 2))}
                  pathOptions={{
                    color: PLATFORM_COLORS[point.platform] || '#888',
                    fillColor: PLATFORM_COLORS[point.platform] || '#888',
                    fillOpacity: 0.6,
                    weight: 1,
                  }}
                >
                  <Popup>
                    <div className="text-sm">
                      <div className="font-semibold capitalize">{point.platform || 'unknown'}</div>
                      <div>{point.count} events &middot; {point.uniqueUsers} unique users</div>
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MetroMap>
          </div>
          {mapPoints.length > 0 && (
            <div className="flex gap-4 mt-3 text-xs text-[var(--color-muted)]">
              <span className="flex items-center gap-1">
                <span className="inline-block w-3 h-3 rounded-full bg-amber-500" /> Web
              </span>
              <span className="flex items-center gap-1">
                <span className="inline-block w-3 h-3 rounded-full bg-blue-500" /> iOS
              </span>
              <span className="flex items-center gap-1">
                <span className="inline-block w-3 h-3 rounded-full bg-emerald-500" /> Android
              </span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
